import type { RealmId } from '@mn/shared';

// ── Encounter tables (per realm) + starter choices. ──
// Town has no wild encounters (safe hub). The Wilds are PvE; the Badlands hold
// the rarest/apex spawns alongside open-PvP risk.

export interface EncounterEntry {
  speciesId: string;
  /** relative weight within the realm's table (higher = more common) */
  weight: number;
  minLevel: number;
  maxLevel: number;
}

export const ENCOUNTERS: Record<RealmId, EncounterEntry[]> = {
  // Safe hub — no wild beasts.
  town: [],

  // PvE hunting grounds: commons + first-stage lines, occasional mid-stage.
  // Starting area — a TIGHT lvl 2-8 band so the early game reads consistently and gold per win is
  // predictable. (The old 2-24 spread mixed lvl-4 and lvl-22 monsters in ONE zone, so a "level 20 area"
  // randomly paid 25 or 100 gold — the reported variance. One area = one level band now.)
  wilds: [
    { speciesId: 'sp_grazeling', weight: 22, minLevel: 2, maxLevel: 6 },
    { speciesId: 'sp_fennec', weight: 20, minLevel: 2, maxLevel: 6 },
    { speciesId: 'sp_emberling', weight: 12, minLevel: 2, maxLevel: 7 },
    { speciesId: 'sp_dripfin', weight: 12, minLevel: 2, maxLevel: 7 },
    { speciesId: 'sp_sproutling', weight: 12, minLevel: 2, maxLevel: 7 },
    { speciesId: 'sp_pebbit', weight: 10, minLevel: 3, maxLevel: 7 },
    { speciesId: 'sp_zephyx', weight: 9, minLevel: 3, maxLevel: 8 },
    { speciesId: 'sp_frostkit', weight: 8, minLevel: 4, maxLevel: 8 },
    { speciesId: 'sp_grublin', weight: 8, minLevel: 4, maxLevel: 8 },
    { speciesId: 'sp_cindermaw', weight: 5, minLevel: 5, maxLevel: 8 },
    { speciesId: 'sp_surgeon', weight: 5, minLevel: 5, maxLevel: 8 },
    { speciesId: 'sp_bramblok', weight: 5, minLevel: 5, maxLevel: 8 },
    { speciesId: 'sp_dunestalker', weight: 4, minLevel: 6, maxLevel: 8 },
  ],

  // The Cave (higher-tier zone, gated at Lv.5). A real high-level band: lower beasts 10-18, mid 18-24,
  // apex 26-35 — so the Cave "lives up to its purpose" (tougher fights, bigger Gold) per player feedback.
  badlands: [
    { speciesId: 'sp_boulderhide', weight: 14, minLevel: 10, maxLevel: 16 },
    { speciesId: 'sp_skytalon', weight: 13, minLevel: 10, maxLevel: 16 },
    { speciesId: 'sp_glacior', weight: 12, minLevel: 12, maxLevel: 18 },
    { speciesId: 'sp_neurolisk', weight: 12, minLevel: 12, maxLevel: 18 },
    { speciesId: 'sp_pyrothar', weight: 8, minLevel: 18, maxLevel: 24 },
    { speciesId: 'sp_leviadon', weight: 8, minLevel: 18, maxLevel: 24 },
    { speciesId: 'sp_grovewarden', weight: 8, minLevel: 18, maxLevel: 24 },
    { speciesId: 'sp_obsidrake', weight: 4, minLevel: 26, maxLevel: 32 },
    { speciesId: 'sp_voidmaw', weight: 4, minLevel: 26, maxLevel: 32 },
    { speciesId: 'sp_aurorath', weight: 3, minLevel: 28, maxLevel: 34 },
    { speciesId: 'sp_titanok', weight: 2, minLevel: 30, maxLevel: 35 },
    // Ultra-rare deep-Cave beasts (new art) — low weight, top levels.
    { speciesId: 'sp_voltbloom', weight: 2, minLevel: 28, maxLevel: 34 },
    { speciesId: 'sp_nyxwing', weight: 2, minLevel: 28, maxLevel: 34 },
    { speciesId: 'sp_voltfox', weight: 2, minLevel: 28, maxLevel: 34 },
    { speciesId: 'sp_cryolith', weight: 1, minLevel: 30, maxLevel: 36 },
    { speciesId: 'sp_ferrok', weight: 1, minLevel: 30, maxLevel: 36 },
    // Mythical: a rare wild Solani baby in the Cave — catch it low and raise it to evolve (Solanoid @30).
    { speciesId: 'sp_solani', weight: 1, minLevel: 10, maxLevel: 16 },
  ],
};

/** The three starter choices offered to a new player (one per element triangle). */
export interface StarterChoice {
  speciesId: string;
  element: 'flame' | 'tide' | 'verdant';
  startLevel: number;
}

export const STARTERS: StarterChoice[] = [
  { speciesId: 'sp_emberling', element: 'flame', startLevel: 5 },
  { speciesId: 'sp_dripfin', element: 'tide', startLevel: 5 },
  { speciesId: 'sp_sproutling', element: 'verdant', startLevel: 5 },
];
